/**
 * Keep keyboard focus out of Flickity cells that are aria-hidden
 *
 * Flickity zet `aria-hidden="true"` op cellen buiten beeld, maar links en knoppen
 * daarin blijven focusbaar: een toetsenbordgebruiker tabt dan naar iets wat een
 * schermlezer verzwijgt (axe: aria-hidden-focus). Bij elke wissel van
 * `aria-hidden` krijgen de focusbare elementen in die cel `tabindex="-1"`, en
 * zodra de cel weer zichtbaar is hun oude tabindex terug. Werkt voor elke slider,
 * ook die later of opnieuw worden opgebouwd, omdat het naar de DOM kijkt in plaats
 * van naar Flickity-events.
 */
const FOCUSABLE = 'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])';
const SAVED = "data-flickity-tabindex";

function focusables(cell) {
    const items = Array.from(cell.querySelectorAll(`${FOCUSABLE}, [${SAVED}]`));

    // Bij een featured-templates-kaart is de cel zelf de link.
    if (cell.matches(FOCUSABLE) || cell.hasAttribute(SAVED)) {
        items.unshift(cell);
    }

    return items;
}

function syncCell(cell) {
    const hidden = cell.getAttribute("aria-hidden") === "true";

    focusables(cell).forEach((el) => {
        if (hidden && !el.hasAttribute(SAVED)) {
            el.setAttribute(SAVED, el.getAttribute("tabindex") ?? "");
            el.setAttribute("tabindex", "-1");
        } else if (!hidden && el.hasAttribute(SAVED)) {
            const original = el.getAttribute(SAVED);
            el.removeAttribute(SAVED);

            // Heeft andere code de tabindex intussen zelf gezet (flickity.js doet dat bij
            // featured-section), dan blijft die staan.
            if (el.getAttribute("tabindex") !== "-1") {
                return;
            }

            if (original === "") {
                el.removeAttribute("tabindex");
            } else {
                el.setAttribute("tabindex", original);
            }
        }
    });
}

function syncSlider(slider) {
    Array.from(slider.children).forEach(syncCell);
}

const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
        const { target } = mutation;

        if (mutation.type === "attributes" && target.parentElement?.classList.contains("flickity-slider")) {
            syncCell(target);
        } else if (mutation.type === "childList") {
            mutation.addedNodes.forEach((node) => {
                if (node.nodeType !== 1) {
                    return;
                }

                if (node.classList.contains("flickity-slider")) {
                    syncSlider(node);
                }

                node.querySelectorAll?.(".flickity-slider").forEach(syncSlider);
            });
        }
    });
});

observer.observe(document.body, {
    attributes: true,
    attributeFilter: ["aria-hidden"],
    childList: true,
    subtree: true,
});

document.querySelectorAll(".flickity-slider").forEach(syncSlider);
